import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("es-ES", {
      month: "short"
    }).toUpperCase();
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return "";
  }
}
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}
function JournalGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col">
            <div className={`${i === 0 ? "aspect-[4/5]" : "aspect-[16/10]"} w-full bg-card animate-pulse`} />
            <div className="mt-6 h-3 w-40 bg-card animate-pulse" />
            <div className="mt-4 h-6 w-full bg-card animate-pulse" />
            <div className="mt-2 h-6 w-3/4 bg-card animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
        No ha sido posible cargar las entradas.
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-sm font-mono uppercase tracking-wider text-muted-foreground">
        No hay artículos publicados actualmente.
      </div>;
  }
  return <div className="grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const isFeatured = i === 0;
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "REFLEXIONES";
      const dateLabel = formatDate(post.date);
      const excerpt = post.excerpt ? stripHtml(post.excerpt) : "";
      return <article data-index={i} key={post.id} className={`group flex flex-col shadow-md hover:shadow-lg rounded-lg py-8 md:py-10 px-6 bg-card/30 transition-shadow duration-300 ${isFeatured ? "lg:row-span-1" : ""}`}>
            <Link to={post.link || "#"} className="block overflow-hidden rounded-lg">
              <AspectRatio ratio={isFeatured ? 4 / 5 : 16 / 10}>
                <div className="h-full w-full overflow-hidden bg-card">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-all duration-500 group-hover:brightness-105" /> : <div className="h-full w-full bg-card" />}
                </div>
              </AspectRatio>
            </Link>

            <div className="mt-7 flex flex-col">
              <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                <span>{category.toUpperCase()}</span>
                {dateLabel && <>
                    <span className="mx-2">—</span>
                    <span>{dateLabel}</span>
                  </>}
              </div>

              <h3 className={`font-serif font-default mt-4 leading-snug line-clamp-2 ${isFeatured ? "text-[26px] md:text-[28px]" : "text-[22px]"} text-foreground`}>
                <Link to={post.link || "#"} className="bg-[length:0%_1px] bg-bottom bg-no-repeat bg-gradient-to-r from-current to-current transition-[background-size] duration-300 group-hover:bg-[length:100%_1px]">
                  {post.title}
                </Link>
              </h3>

              {excerpt && isFeatured && <p className="font-serif mt-4 text-[15px] leading-relaxed line-clamp-3 text-muted-foreground">
                  {excerpt}
                </p>}

              <div className="mt-6">
                <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-opacity hover:opacity-60 text-foreground" aria-label={`Leer ${post.title}`}>
                  <span>Leer artículo</span>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Journal() {
  const wp_query_params = {
    post_type: "post",
    per_page: 100,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="3562" id="archivo-de-artículos" className="w-full bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between md:gap-12">
          <div className="flex flex-col md:max-w-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              ARCHIVO DE ARTÍCULOS — 06
            </span>
            <h2 className="font-serif font-default font-semibold mt-6 md:mb-12 text-[42px] leading-[1.05] md:text-[52px] lg:text-[56px] text-foreground">Artículos y conferencias.</h2>
          </div>
        </div>

        <div className="mt-14 mb-16 h-px w-full md:mt-16 md:mb-20 bg-border" />

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 100,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <JournalGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}